import React, { useState, useEffect } from 'react'
import { Box, Stack, Typography, alpha, useTheme } from '@mui/material'
import { gql, useQuery } from '@apollo/client'

import BuildingMap from 'packages/wimap/Indoor'

import Device from './Device'
import { deviceToMarker } from '../use-tree'
import { useAccessPointZonesContext } from '../AccessPointZonesContext'
import { useTranslation } from 'react-i18next'

export const OPERATION_IMAGE = gql`
  query GetDownloadMapImage($id: String!) {
    image: downloadMapImage(uuid: $id) {
      ... on Image {
        dataURL
        width
        height
      }
    }
  }
`

const DISCLAIMER_FADEOUT_DELAY = 5000
const DISCLAIMER_FADEOUT_DURATION = 500

const Disclaimer = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [isVisible, setIsVisible] = useState(true)
  const [isMounted, setIsMounted] = useState(true)

  useEffect(() => {
    const fadeoutTimer = setTimeout(() => {
      setIsVisible(false)
    }, DISCLAIMER_FADEOUT_DELAY)

    const unmountTimer = setTimeout(() => {
      setIsMounted(false)
    }, DISCLAIMER_FADEOUT_DELAY + DISCLAIMER_FADEOUT_DURATION)

    return () => {
      clearTimeout(fadeoutTimer)
      clearTimeout(unmountTimer)
    }
  }, [])

  if (!isMounted) {
    return null
  }

  return (
    <Stack sx={{
      position: 'absolute',
      right: 20,
      top: 20,
      zIndex: 1000,
      borderRadius: 3,
      orderColor: 'border.primary',
      backgroundColor: 'white',
      padding: 1,
      paddingLeft: 2,
      paddingRight: 2,
      boxShadow: `0 12px 16px -8px ${alpha(
        theme.palette.common.black,
        0.24,
      )}`,
      opacity: isVisible ? 1 : 0,
      transition: `opacity ${DISCLAIMER_FADEOUT_DURATION}ms ease-out`,
    }}>
      <Box sx={{ fontSize: 13 }} dangerouslySetInnerHTML={{ __html: t('access_point_zones.map.disclaimer') }} />
    </Stack>
  )
}

const BodyMap = ({ image, loading }: { image: string, loading: boolean }) => {
  const { devicesOnMap, onFloorPrev, onFloorNext } = useAccessPointZonesContext()
  const { t } = useTranslation()
  const theme = useTheme()
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Disclaimer />
      <Stack
        sx={{
          position: 'absolute',
          bottom: 20,
          left: 312,
          zIndex: 1000,
          borderRadius: 8,
          backgroundColor: 'white',
          padding: 1,
          paddingLeft: 2,
          paddingRight: 2,
          border: 'solid 1px',
          borderColor: 'border.primary',
          boxShadow: `0 12px 16px -8px ${alpha(
            theme.palette.common.black,
            0.24,
          )}`
        }} direction={'row'} gap={3} alignItems={'center'}
      >
        <Typography sx={{ fontSize: 13 }}>{t('access_point_zones.legend.ap')}</Typography>
        <i className={'fa-light fa-signal-stream-slash'} />
      </Stack>
      <BuildingMap
        loading={loading}
        breadcrumbProps={{
          showBreadcrumb: false,
        }}
        legendProps={{
          mapsView: 'global'
        }}
        floorsNavigationProps={{
          canSee: true,
          onPrev: onFloorPrev,
          onNext: onFloorNext,
        }}
        image={image || { width: 500, height: 500 }}
        defaultGrayScale={true}
        hasOpenCard={false}
      >
        {
          devicesOnMap.map(deviceToMarker).map(device => (
            <Device device={device} key={device.uuid} />
          ))
        }
      </BuildingMap>
    </Box>
  )
}

const Body = () => {
  const { mapUuid } = useAccessPointZonesContext()

  const { data, loading } = useQuery(OPERATION_IMAGE, {
    variables: {
      id: mapUuid
    },
    skip: !mapUuid
  })

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <BodyMap image={data?.image} loading={loading || !mapUuid} key={mapUuid} />
    </Box>
  )
}

export default Body
